import orderBy from "lodash.orderby";
import { PanelLeftOpenIcon } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  IgAvatar,
  IgChip,
  IgIconButton,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { OrganizationListDto } from "@intelligems/ig-types";
import { Box, Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useSwitchOrganization } from "@src/api/organizations/switch-organization";
import { useGetOrgList } from "@src/api/organizations-list/get-organization-list";
import { OrgList } from "@src/components/Navigation/OrgPicker/OrgList";
import { IgWhiteGemIcon } from "@src/images/Icons/Intelligems/IgWhiteGem";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { setSideNavCollapsed } from "@src/store/slices/appSettings";
import { getAuthToken } from "@src/utils";
import { SideNavCollapseControl } from "@src/utils/SideNavCollapseControl";

interface OrgPickerProps {
  redirectToHome?: boolean;
  hidePicker?: boolean;
  hideCollapseControl?: boolean;
  drawerOpen: boolean;
  settingsPage?: boolean;
}

const PREFIX = "OrgPicker";

const classes = {
  orgName: `${PREFIX}-orgName`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.orgName}`]: {
    fontWeight: 500,
    wordBreak: "break-word",
    overflow: "hidden",
  },
  display: "flex",
  flexDirection: "column",
  // padding: "16px 8px",
}));

export const OrgPicker = ({
  redirectToHome,
  hidePicker,
  drawerOpen,
  hideCollapseControl,
  settingsPage,
}: OrgPickerProps) => {
  const theme = useTheme();
  const organization = useGetOrg();
  const navigate = useNavigate();
  const orgList = useGetOrgList();
  const switchOrganization = useSwitchOrganization();

  const dispatch = useDispatch();

  const handleChange = (value: OrganizationListDto | null) => {
    if (!value) return;

    switchOrganization.mutate({
      token: getAuthToken() || "",
      orgId: value.id,
    });
  };

  const value = {
    id: organization.data.org.id,
    name: organization.data.org.name,
    isInstalled: organization.data.org["isInstalled"] || true,
    shopId: organization.data.org.shopId!,
    createdAtTs: organization.data.org.createdAtTs || 0,
    roles: [],
  };

  return (
    <StyledBox
      data-clarity-unmask={true}
      sx={{
        // height: hidePicker ? "120px" : undefined,
        cursor: redirectToHome
          ? "pointer"
          : window.location.pathname === GENERAL_ROUTES.Index
            ? "auto"
            : "pointer",
        zIndex: 1360,
        py: 4,
        px: drawerOpen ? 2 : 4,
        // px: drawerOpen ?
      }}
    >
      <Stack direction={"column"} spacing={4}>
        <Grid
          direction={"row"}
          container
          justifyContent={"space-between"}
          wrap={"nowrap"}
          alignItems={"center"}
        >
          {import.meta.env.VITE_UI_URL === "https://beta.intelligems.io" && (
            <IgChip
              color={"accent"}
              label={"Beta"}
              size={"small"}
              sx={{
                position: "absolute",
                top: 45,
                right: 19,
                minWidth: "45px",
                fontSize: "10px",
                zIndex: 999999,
              }}
            />
          )}

          <Grid
            container
            direction={"row"}
            spacing={2}
            justifyContent={"flex-start"}
            alignItems={"center"}
            sx={{
              cursor: "pointer",
            }}
          >
            <Grid>
              <IgAvatar
                onClick={() => {
                  if (settingsPage) {
                    void navigate(GENERAL_ROUTES.Index);
                  } else {
                    dispatch(setSideNavCollapsed());
                  }
                }}
                variant="rounded"
                sx={{
                  backgroundColor: theme.igPrimitives.base[500],
                  transition: "250ms ease",

                  ".IgIcon": { opacity: 1, transition: "250ms ease" },

                  ".ExpandButton": {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    opacity: 0,
                    transition: "250ms ease",
                  },

                  ...(!drawerOpen && {
                    "&:hover": {
                      backgroundColor: theme.igBackgroundColors.baseLightMid,
                    },

                    "&:hover .ExpandButton": {
                      opacity: 1,
                    },

                    "&:hover .IgIcon": {
                      opacity: 0,
                    },
                  }),
                }}
              >
                <IgWhiteGemIcon className="IgIcon" color={"dark"} />

                {!drawerOpen && (
                  <IgTooltip placement="right" title="Expand Navigation">
                    <IgIconButton className="ExpandButton">
                      <PanelLeftOpenIcon size={16} />
                    </IgIconButton>
                  </IgTooltip>
                )}
              </IgAvatar>
            </Grid>

            {drawerOpen && (
              <Grid>
                <IgTypography
                  variant={"h2"}
                  fontWeight={600}
                  onClick={() => {
                    if (redirectToHome) {
                      void navigate(GENERAL_ROUTES.Index);
                    } else {
                      if (window.location.pathname !== GENERAL_ROUTES.Index) {
                        void navigate(GENERAL_ROUTES.Index);
                      }
                    }
                  }}
                >
                  intelligems
                </IgTypography>
              </Grid>
            )}
          </Grid>
          {drawerOpen && !hideCollapseControl && <SideNavCollapseControl />}
        </Grid>
        {hidePicker || !drawerOpen ? null : (
          <Grid container>
            {orgList.isSuccess && orgList.data.orgs ? (
              orgList.data.orgs.length > 1 && !redirectToHome ? (
                <Grid
                  container
                  direction={"row"}
                  flexWrap={"nowrap"}
                  columnSpacing={1}
                  sx={{ width: "100%" }}
                >
                  <OrgList onChange={handleChange} value={value} />
                </Grid>
              ) : (
                <Grid container justifyContent={"start"}>
                  <Grid
                    sx={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      paddingLeft: 4,
                    }}
                  >
                    <IgTypography className={classes.orgName}>
                      {organization.data.org.name}
                    </IgTypography>
                  </Grid>
                </Grid>
              )
            ) : (
              <Grid sx={{ marginTop: "-13px" }}>
                <Skeleton
                  sx={{
                    height: "39px",
                    width: "160px",
                    backgroundColor: "rgb(73 76 80);",
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Stack>
    </StyledBox>
  );
};
